import React from "react";
import { cx } from "../../features/ui/Card";

function sanitizeDecimalInput(raw, { maxDp = 2, maxIntDigits = 3 } = {}) {
  let s = String(raw ?? "").replace(/[^\d.]/g, "");

  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    const before = s.slice(0, firstDot + 1);
    const after = s.slice(firstDot + 1).replace(/\./g, "");
    s = before + after;
  }

  const parts = s.split(".");
  let intPart = parts[0] ?? "";
  let fracPart = parts[1] ?? "";

  if (maxIntDigits != null) intPart = intPart.slice(0, maxIntDigits);
  if (maxDp != null) fracPart = fracPart.slice(0, maxDp);

  if (s.includes(".")) return `${intPart}.${fracPart}`;
  return intPart;
}

function sanitizeIntInput(raw) {
  return String(raw ?? "").replace(/[^\d]/g, "");
}

function toFiniteNumber(v, fallback) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export default function ModuleTable({
  modules,
  minGrade,
  onUpdateModule,
  onClamp,
  onRemoveModule,
}) {
  return (
    <div className="mt-4">
      <table className="w-full table-fixed border-separate border-spacing-y-2">
        {/* Adjusted widths:
            - Slider column smaller
            - Grade column bigger
        */}
        <colgroup>
          <col className="w-[15%] sm:w-[30%]" />
          <col className="w-[15%] sm:w-[13%]" />
          <col className="w-[30%] sm:w-[20%]" />
          <col className="w-[20%] sm:w-[15%]" />
          <col className="w-[10%] sm:w-[15%]" />
        </colgroup>

        <thead>
          <tr className="text-left text-[11px] sm:text-xs font-semibold text-neutral-600 dark:text-neutral-400">
            <th className="px-2 sm:px-3">Module</th>
            <th className="px-2 sm:px-3">Credits</th>
            <th className="px-2 sm:px-3">Grade</th>
            <th className="px-2 sm:px-3">%</th>
            <th className="px-2 sm:px-3"></th>
          </tr>
        </thead>

        <tbody>
          {modules.map((m, idx) => {
            const gradeNum = toFiniteNumber(m.grade, minGrade);
            const sliderValue = Math.max(minGrade, Math.min(100, gradeNum));

            return (
              <tr
                key={idx}
                className="rounded-xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-950"
              >
                {/* More left padding on mobile so it's not hugging the edge */}
                <td className="pl-4 pr-2 sm:px-3 py-2 text-sm font-medium text-neutral-800 dark:text-neutral-100 whitespace-nowrap">
                  <span className="sm:hidden">M{idx + 1}</span>
                  <span className="hidden sm:inline">Module {idx + 1}</span>
                </td>

                {/* Credits */}
                <td className="px-2 sm:px-3 py-2">
                  <input
                    type="text"
                    value={m.credits}
                    onChange={(e) =>
                      onUpdateModule(idx, "credits", sanitizeIntInput(e.target.value))
                    }
                    onBlur={() => onClamp(idx, "credits")}
                    inputMode="numeric"
                    className="
                      w-full max-w-[44px] sm:max-w-none sm:w-14
                      rounded-lg border border-neutral-200 bg-white px-1.5 py-1 text-sm
                      focus:border-neutral-400 focus:outline-none
                      dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-50
                    "
                    placeholder="15"
                    aria-label={`Credits for module ${idx + 1}`}
                  />
                </td>

                <td className="px-1 sm:px-3 py-2">
                  <div className="w-full">
                    <input
                      type="range"
                      min={minGrade}
                      max={100}
                      step={1}
                      value={sliderValue}
                      onChange={(e) => onUpdateModule(idx, "grade", String(e.target.value))}
                      onMouseUp={() => onClamp(idx, "grade")}
                      onTouchEnd={() => onClamp(idx, "grade")}
                      className="w-full h-1 sm:h-2"
                      aria-label={`Grade slider for module ${idx + 1}`}
                    />

                    {/* Mobile labels */}
                    <div className="mt-0.5 flex sm:hidden w-full items-center justify-between text-[9px] text-neutral-600 dark:text-neutral-400">
                      <span className="tabular-nums">{minGrade}</span>
                      <span className="tabular-nums font-medium text-neutral-800 dark:text-neutral-200">
                        {Math.round(sliderValue)}%
                      </span>
                      <span className="tabular-nums">100</span>
                    </div>

                    {/* Desktop labels */}
                    <div className="mt-1 hidden sm:flex w-full items-center justify-between text-[11px] text-neutral-600 dark:text-neutral-400">
                      <span className="tabular-nums">{minGrade}</span>
                      <span className="tabular-nums font-medium text-neutral-800 dark:text-neutral-200">
                        {Math.round(sliderValue)}%
                      </span>
                      <span className="tabular-nums">100</span>
                    </div>
                  </div>
                </td>

                {/* Grade (bigger: wider max + a touch more padding) */}
                <td className="px-2 sm:px-3 py-2">
                  <input
                    type="text"
                    value={m.grade}
                    onChange={(e) =>
                      onUpdateModule(
                        idx,
                        "grade",
                        sanitizeDecimalInput(e.target.value, { maxDp: 2, maxIntDigits: 3 })
                      )
                    }
                    onBlur={() => onClamp(idx, "grade")}
                    inputMode="decimal"
                    className="
                      w-full max-w-[72px] sm:max-w-none sm:w-14
                      rounded-lg border border-neutral-200 bg-white px-2 py-1 text-sm
                      focus:border-neutral-400 focus:outline-none
                      dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-50
                    "
                    placeholder={String(minGrade)}
                    aria-label={`Grade input for module ${idx + 1}`}
                  />
                </td>

                {/* Remove */}
                <td className="py-2 text-right pr-6 sm:pr-3">
                  {/* Mobile: icon button */}
                  <button
                    type="button"
                    onClick={() => onRemoveModule(idx)}
                    disabled={modules.length <= 0}
                    className={cx(
                      "inline-flex sm:hidden h-7 w-7 items-center justify-center rounded-lg transition",
                      "text-neutral-600 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-900",
                      "disabled:opacity-50 disabled:hover:bg-transparent"
                    )}
                    aria-label={`Remove module ${idx + 1}`}
                    title="Remove"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4"
                      aria-hidden="true"
                    >
                      <path d="M18 6L6 18" />
                      <path d="M6 6l12 12" />
                    </svg>
                  </button>

                  {/* Desktop: text button */}
                  <button
                    type="button"
                    onClick={() => onRemoveModule(idx)}
                    disabled={modules.length <= 0}
                    className={cx(
                      "hidden sm:inline-flex rounded-xl px-3 py-2 text-sm font-medium transition",
                      "text-neutral-700 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-900",
                      "disabled:opacity-50 disabled:hover:bg-transparent"
                    )}
                  >
                    Remove
                  </button>
                </td>

              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="sm:hidden mt-2 text-[11px] text-neutral-600 dark:text-neutral-400">
        Tip: drag the slider; exact % is shown in the Grade field.
      </div>
    </div>
  );
}
